/**
 * Per-step drafts in localStorage so a reload loses nothing (AC20, web side). Only what the member typed in the
 * fields: name, email, the city query and the note. Never the code, the password or anything biometric.
 */
export interface DraftCity {
  id: number
  name: string
  admin1: string | null
  country: string
}

export interface Draft {
  name?: string
  email?: string
  cityQuery?: string
  note?: string
  /** The place the member chose in S3 (so a reload keeps the selection, not just its label). */
  city?: DraftCity
  /** Epoch ms from which "Send the code again" is allowed (so a reload keeps the countdown). */
  resendAt?: number
}

const KEY = 'ipc:draft:v1'
const ALLOWED: ReadonlyArray<keyof Draft> = ['name', 'email', 'cityQuery', 'note', 'resendAt', 'city']

function store(): Storage | undefined {
  try {
    return window.localStorage
  } catch {
    return undefined
  }
}

function isCity(v: unknown): v is DraftCity {
  if (!v || typeof v !== 'object') return false
  const c = v as Record<string, unknown>
  return (
    typeof c.id === 'number' &&
    typeof c.name === 'string' &&
    typeof c.country === 'string' &&
    (c.admin1 === null || typeof c.admin1 === 'string')
  )
}

export function readDraft(): Draft {
  try {
    const raw = store()?.getItem(KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const out: Draft = {}
    for (const k of ALLOWED) {
      const v = parsed[k]
      if (k === 'city') {
        if (isCity(v)) out.city = v
      } else if (k === 'resendAt' ? typeof v === 'number' : typeof v === 'string')
        (out as Record<string, unknown>)[k] = v
    }
    return out
  } catch {
    return {}
  }
}

export function saveDraft(patch: Draft): void {
  try {
    store()?.setItem(KEY, JSON.stringify({ ...readDraft(), ...patch }))
  } catch {
    // storage disabled or full: the registration cookie still keeps the step
  }
}

/** Remove the named fields (or everything) once the step they belong to is saved. */
export function clearDraft(...keys: Array<keyof Draft>): void {
  try {
    if (keys.length === 0) {
      store()?.removeItem(KEY)
      return
    }
    const current: Record<string, unknown> = { ...readDraft() }
    for (const k of keys) delete current[k]
    store()?.setItem(KEY, JSON.stringify(current))
  } catch {
    // ignore
  }
}
